import { useEffect, useRef } from "react";
import { nanoid } from "nanoid";
import {
  isSensitiveTelemetryKey,
  redactSensitive,
  redactTelemetryValue,
} from "@kudoz/shared/redactDiagnostics";

export interface AttributionCtx {
  sessionId: string;
  source?: string;
  channel?: string;
  shortcode?: string;
  postId?: string;
  reviewId?: string;
  restaurantId?: string;
  /** Short-link placement (fb_comment | ig_comment | ig_bio | tt_bio) — stamped by /go/:code */
  placement?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const SESSION_KEY = "kudoz_session_id";
const ATTRIBUTION_KEY = "kudoz_attribution";

function initSession(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = nanoid(21);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function loadStored(): Partial<AttributionCtx> {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {};
      }
      const safe: Partial<AttributionCtx> = {};
      for (const [key, value] of Object.entries(parsed)) {
        // Session storage can outlive a prior release. Do not let an unknown
        // inviteToken property become part of a later analytics payload.
        if (isSensitiveTelemetryKey(key) || typeof value !== "string") continue;
        const clean = redactSensitive(value);
        if (clean !== value) continue;
        if (
          [
            "sessionId",
            "source",
            "channel",
            "shortcode",
            "postId",
            "reviewId",
            "restaurantId",
            "placement",
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
          ].includes(key)
        ) {
          (safe as Record<string, string>)[key] = value;
        }
      }
      return safe;
    }
  } catch {
    // ignore
  }
  return {};
}

function saveStored(ctx: Partial<AttributionCtx>): void {
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(ctx));
  } catch {
    // ignore
  }
}

function parseUrlParams(): Partial<AttributionCtx> {
  try {
    const params = new URLSearchParams(window.location.search);
    const result: Partial<AttributionCtx> = {};
    const pick = (key: keyof AttributionCtx, param: string) => {
      const val = params.get(param);
      if (val) {
        const clean = redactSensitive(val);
        if (clean === val) (result as Record<string, string>)[key] = val;
      }
    };
    pick("utm_source", "utm_source");
    pick("utm_medium", "utm_medium");
    pick("utm_campaign", "utm_campaign");
    pick("utm_term", "utm_term");
    pick("utm_content", "utm_content");
    pick("shortcode", "shortcode");
    pick("placement", "placement");
    pick("postId", "post_id");
    pick("reviewId", "review_id");
    pick("restaurantId", "restaurant_id");
    pick("channel", "platform");
    pick("source", "source");
    // Infer source from utm params if not explicitly set
    const utmSrc = params.get("utm_source");
    if (utmSrc === "kudoz_short" && !result.source) {
      result.source = "short_link";
    }
    return result;
  } catch {
    return {};
  }
}

// Build the full context for this session
function buildCtxUncached(): AttributionCtx {
  const sessionId = initSession();
  const stored = loadStored();
  const fromUrl = parseUrlParams();
  // Merge: URL params take priority; don't overwrite with nulls
  const merged: Partial<AttributionCtx> = { ...stored };
  for (const [k, v] of Object.entries(fromUrl)) {
    if (v) (merged as Record<string, string>)[k] = v;
  }
  saveStored(merged);
  return { sessionId, ...merged };
}

// Module-level cache: rebuild only when the URL search string changes.
// This avoids re-reading sessionStorage / re-parsing the URL on every
// render of every card in long feeds.
let _cachedCtx: AttributionCtx | null = null;
let _cachedSearch: string | null = null;

function buildCtx(): AttributionCtx {
  const search =
    typeof window !== "undefined" ? window.location.search : "";
  if (_cachedCtx && _cachedSearch === search) {
    return _cachedCtx;
  }
  _cachedCtx = buildCtxUncached();
  _cachedSearch = search;
  return _cachedCtx;
}

// Module-level singleton so the provider can be set once
let _ctxProvider: (() => AttributionCtx) | null = null;

export function setAttributionCtxProvider(fn: () => AttributionCtx): void {
  _ctxProvider = fn;
}

export function getAttributionCtxGlobal(): AttributionCtx | null {
  return _ctxProvider ? _ctxProvider() : null;
}

// ---------------------------------------------------------------------------
// Batching queue — accumulate events and flush periodically or on page unload
// ---------------------------------------------------------------------------

interface QueuedEvent {
  eventType: string;
  clientEventId: string;
  entityType?: string | null;
  entityId?: string | null;
  sessionId?: string;
  shortLinkCode?: string;
  placement?: string;
  sourcePostId?: string;
  sourceReviewId?: string;
  sourceType?: string;
  campaignId?: string;
  metadata?: Record<string, string>;
  channel?: string;
  [key: string]: unknown;
}

const _eventQueue: QueuedEvent[] = [];

function buildEventPayload(
  eventType: string,
  entityType?: string,
  entityId?: string,
  ctx?: AttributionCtx | null,
  extras?: Record<string, unknown>
): QueuedEvent {
  const safeString = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    const clean = redactSensitive(value);
    return clean === value ? value : undefined;
  };
  const safeExtras = extras
    ? (redactTelemetryValue(extras) as Record<string, unknown>)
    : {};
  const safeEntityId =
    entityType && /invite/i.test(entityType) ? undefined : safeString(entityId);
  return {
    eventType: safeString(eventType) ?? "unknown",
    clientEventId: nanoid(21),
    entityType: safeString(entityType) || null,
    entityId: safeEntityId || null,
    sessionId: safeString(ctx?.sessionId),
    shortLinkCode: safeString(ctx?.shortcode),
    placement: safeString(ctx?.placement),
    sourcePostId: safeString(ctx?.postId),
    sourceReviewId: safeString(ctx?.reviewId),
    sourceType: safeString(ctx?.source),
    campaignId: safeString(ctx?.utm_campaign),
    metadata: {
      ...(safeString(ctx?.utm_source)
        ? { utm_source: safeString(ctx?.utm_source) }
        : {}),
      ...(safeString(ctx?.utm_medium)
        ? { utm_medium: safeString(ctx?.utm_medium) }
        : {}),
      ...(safeString(ctx?.utm_campaign)
        ? { utm_campaign: safeString(ctx?.utm_campaign) }
        : {}),
      ...(safeString(ctx?.utm_term)
        ? { utm_term: safeString(ctx?.utm_term) }
        : {}),
      ...(safeString(ctx?.utm_content)
        ? { utm_content: safeString(ctx?.utm_content) }
        : {}),
    },
    channel: safeString(ctx?.channel),
    ...safeExtras,
  };
}

function getAuthToken(): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem("auth_token")
      : null;
  } catch {
    return null;
  }
}

// Server-side Zod schema caps a single batch at 200 events.
const ATTRIBUTION_BATCH_CHUNK_SIZE = 200;

/**
 * Flush all queued attribution events to the batch endpoint.
 *
 * Events are sent in chunks of up to ATTRIBUTION_BATCH_CHUNK_SIZE so that a
 * large queue never exceeds the server's per-request validation limit.
 *
 * On the page-unload path (`useBeacon = true`) each chunk is submitted via
 * `navigator.sendBeacon` so it can be queued after the page is torn down.
 * If sendBeacon returns `false` (browser quota exceeded or unavailable),
 * the chunk falls back to `fetch` with `keepalive: true`, which also
 * survives page unload within the browser's keepalive budget.
 */
export function flushAttributionQueue(useBeacon = false): void {
  if (_eventQueue.length === 0) return;
  const events = _eventQueue.splice(0, _eventQueue.length);

  for (let i = 0; i < events.length; i += ATTRIBUTION_BATCH_CHUNK_SIZE) {
    const chunk = events.slice(i, i + ATTRIBUTION_BATCH_CHUNK_SIZE);
    const body = JSON.stringify({ events: chunk });

    // Try sendBeacon on the unload path first
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const queued = navigator.sendBeacon("/api/attribution/events", blob);
      if (queued) continue; // successfully handed off to the browser
      // Fall through: beacon quota exceeded or not supported — use fetch
    }

    // Regular fetch with keepalive so the request survives page navigation
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      fetch("/api/attribution/events", {
        method: "POST",
        headers,
        body,
        keepalive: true, // allows the request to outlive the page
      }).catch(() => {
        // fire-and-forget — never throw
      });
    } catch {
      // ignore
    }
  }
}

// Wire up the flush timer and page-unload handler once at module load.
if (
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  typeof document.addEventListener === "function"
) {
  // Flush every 5 seconds. Call .unref() if available (Node.js) so the timer
  // does not prevent the process from exiting in test environments.
  const _flushTimer = setInterval(() => flushAttributionQueue(false), 5000);
  if (
    typeof _flushTimer === "object" &&
    _flushTimer !== null &&
    typeof (_flushTimer as unknown as { unref?: () => void }).unref === "function"
  ) {
    (_flushTimer as unknown as { unref: () => void }).unref();
  }

  // Flush on page hide/unload using sendBeacon for reliable delivery
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushAttributionQueue(true);
    }
  });
}

/**
 * Push an attribution event onto the module-level queue.
 * The queue is flushed every 5 seconds or on page unload.
 */
function enqueueAttributionEvent(
  eventType: string,
  entityType?: string,
  entityId?: string,
  ctx?: AttributionCtx,
  extras?: Record<string, unknown>
): void {
  try {
    const attribution = ctx || getAttributionCtxGlobal();
    _eventQueue.push(
      buildEventPayload(eventType, entityType, entityId, attribution, extras)
    );
  } catch {
    // fire-and-forget — never throw
  }
}

export function useAttribution(): {
  getAttributionCtx: () => AttributionCtx;
  sendAttributionEvent: (
    eventType: string,
    entityType?: string,
    entityId?: string,
    extras?: Record<string, unknown>
  ) => void;
} {
  const ctx = buildCtx();

  const getAttributionCtx = (): AttributionCtx => buildCtx();

  const sendAttributionEvent = (
    eventType: string,
    entityType?: string,
    entityId?: string,
    extras?: Record<string, unknown>
  ): void => {
    enqueueAttributionEvent(eventType, entityType, entityId, ctx, extras);
  };

  return { getAttributionCtx, sendAttributionEvent };
}

/**
 * Attach the returned ref to a DOM node. The given attribution event will
 * fire exactly once — the first time the node becomes ≥50% visible in the
 * viewport. This replaces the previous "fire on mount" pattern that caused
 * a request storm of dozens of POSTs at feed load. The observer is
 * disconnected immediately after firing.
 *
 * If `entityId` is empty/undefined, nothing fires. If IntersectionObserver
 * is unavailable, the event fires immediately as a fallback.
 */
export function useAttributionOnView<T extends Element>(
  eventType: string,
  entityType: string,
  entityId: string | undefined,
): React.MutableRefObject<T | null> {
  const ref = useRef<T | null>(null);
  const ctx = buildCtx();
  // Hold latest ctx in a ref so the effect only re-runs on entityId changes.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    if (!entityId) return;
    const el = ref.current;
    if (!el) return;

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      enqueueAttributionEvent(eventType, entityType, entityId, ctxRef.current);
    };

    if (typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            fire();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: [0.5] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eventType, entityType, entityId]);

  return ref;
}
